import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { signUpCorporateUser } from '@/api/signUp/signUp.actions.ts';
import { CorporateRegisterData } from '@/api/signUp/signUp.types.ts';
import Button from '@/components/controls/Button.tsx';
import {
  defaultInputStyle,
  defaultLabelStyle,
} from '@/components/controls/CustomEntityFormField.tsx';
import CustomInput from '@/components/controls/CustomInput.tsx';
import PasswordEyeIcon from '@/components/controls/images/input/PasswordEyeIcon.tsx';
import MobilePhoneInput from '@/components/controls/MobilePhoneInput.tsx';
import { CorporateConfirmPasswordSchema } from '@/components/zod/types.ts';
import { useAuthStore } from '@/stores/useAuthStore.ts';

const CorporateRegisterForm: FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CorporateRegisterData>({
    resolver: zodResolver(CorporateConfirmPasswordSchema),
    mode: 'onChange',
  });

  const [isPassword, setIsPassword] = useState(true);
  const [isConfirmPassword, setIsConfirmPassword] = useState(true);
  const router = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const { logout } = useAuthStore();

  const switchIsPassword = (): void => {
    setIsPassword(!isPassword);
  };

  const onSubmit = async (data: CorporateRegisterData): Promise<void> => {
    const response = await signUpCorporateUser(data, token as string);
    if (!response.success) {
      toast.error(response.error.message);
      return;
    }

    toast.success('You’re all set!');
    router('/');
  };

  useEffect(() => {
    if (!token) {
      return;
    }

    logout();
    localStorage.setItem('token', token);
  }, [token]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-5">
        <CustomInput
          name="name"
          type="text"
          hasIcon={false}
          label="Contact Name"
          control={control}
          error={errors.name}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <CustomInput
          name="surname"
          type="text"
          hasIcon={false}
          label="Contact Surname"
          control={control}
          error={errors.surname}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <CustomInput
          name="referredBy"
          type="text"
          hasIcon={false}
          label="Referred By"
          control={control}
          error={errors.referredBy}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <CustomInput
          name="email"
          type="email"
          hasIcon={false}
          label="Email"
          control={control}
          error={errors.email}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <MobilePhoneInput
          name="mobileNumber"
          label="Mobile Number"
          control={control}
          error={errors.mobileNumber}
          inputClassName={defaultInputStyle}
          labelClassName={defaultLabelStyle}
        />
        <CustomInput
          name="companyName"
          type="text"
          hasIcon={false}
          label="Company Name"
          control={control}
          error={errors.companyName}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <CustomInput
          name="companyRegNo"
          type="text"
          hasIcon={false}
          label="Company Registration Number"
          control={control}
          error={errors.companyRegNo}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <CustomInput
          name="password"
          type={isPassword ? 'password' : 'text'}
          hasIcon
          label="Password"
          icon={<PasswordEyeIcon />}
          onIconClick={switchIsPassword}
          control={control}
          error={errors.password}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <CustomInput
          name="confirmPassword"
          type={isConfirmPassword ? 'password' : 'text'}
          hasIcon
          label="Confirm Password"
          icon={<PasswordEyeIcon />}
          onIconClick={() => setIsConfirmPassword(!isConfirmPassword)}
          control={control}
          error={errors.confirmPassword}
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
        />
        <Button
          buttonStyle="justify-center items-center px-6 py-2 mt-5 text-base font-semibold text-white whitespace-nowrap bg-honoursOrange rounded-xl max-md:px-5 w-full text-lg"
          text="Create an Account"
          buttonType={{ type: 'submit' }}
          hasImage={false}
        />
      </div>
    </form>
  );
};

export default CorporateRegisterForm;
